import api from './api';

export interface HiddenIncome {
  id: string;
  bookingRef: string;
  incomeType: string;
  amount: string;
  description: string;
  customerName: string;
  unitRef: string;
  incomeDate: string;
  collectedAmount: string;
  pendingAmount: string;
  isApproved: boolean;
  approvedBy?: string;
  approvedAt?: string;
  createdAt: string;
  project?: {
    id: number;
    name: string;
  } | null;
}

export interface BookingWithHiddenIncome {
  id: string;
  bookingId: string;
  customerName: string;
  contactNumber: string;
  unitRef: string;
  bookingDate: string;
  totalAmount: number;
  chargesAmount?: string;
  terraceAmount?: string;
  otherWorkAmount?: string;
  banakhatAmount?: string;
  status: string;
  project?: {
    id: number;
    name: string;
    isActive: boolean;
    createdAt: string;
    updatedAt: string;
    createdBy: string;
    updatedBy: string;
  };
  unit?: {
    id: number;
    projectId: number;
    unitType: string;
    unitNo: string;
    wing: string;
    size: string;
    currentStatus: string;
    saleDeedAmount: number;
    extraWorkAmount?: number;
    terraceStatus: string;
    terraceSize?: string;
    extraSpace?: string;
    extraSpaceAmount?: number;
    createdAt: string;
    updatedAt: string;
    createdBy: string;
    updatedBy: string;
  };
  applicants?: Array<{
    id: number;
    bookingId: number;
    firstName: string;
    fatherHusbandName: string;
    surname: string;
    contactNumber: string;
    isPrimary: boolean;
    createdAt: string;
    updatedAt: string;
  }>;
  mainIncomeReceived?: number;
  hiddenIncomeAggregates?: {
    totalExpectedAmount: number;
    totalReceivedAmount: number;
    totalPendingAmount: number;
    recordCount: number;
  };
}

export interface CreateHiddenIncomeData {
  bookingId: string;
  incomeType: string;
  amount: string;
  collectedAmount: string;
  description: string;
  incomeDate: string;
}

class HiddenIncomeService {
  // Helper to get headers with hidden mode flag
  private getHiddenModeHeaders() {
    return {
      'x-hidden-mode': 'true'
    };
  }

  // Search bookings for hidden income
  async searchBookings(searchTerm: string, searchType: 'customer' | 'unit' | 'both'): Promise<BookingWithHiddenIncome[]> {
    const params = new URLSearchParams({
      searchTerm: searchTerm.trim(),
      searchType
    });

    const response = await api.get(`/hidden-income/bookings/search?${params}`, {
      headers: this.getHiddenModeHeaders()
    });
    return response.data.data || [];
  }

  // Get all hidden incomes with pagination and filters
  async getHiddenIncomes(params?: {
    page?: number;
    limit?: number;
    incomeType?: string;
    search?: string;
    projectId?: number;
  }): Promise<{
    data: HiddenIncome[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      pages: number;
    };
  }> {
    const queryParams = new URLSearchParams();
    
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.incomeType) queryParams.append('incomeType', params.incomeType);
    if (params?.search) queryParams.append('search', params.search);
    if (params?.projectId) queryParams.append('projectId', params.projectId.toString());
    
    const url = queryParams.toString() ? `/hidden-income?${queryParams}` : '/hidden-income';
    
    const response = await api.get(url, {
      headers: this.getHiddenModeHeaders()
    });
    
    return {
      data: response.data.data || [],
      pagination: response.data.pagination || {
        page: 1,
        limit: 50,
        total: 0,
        pages: 0
      }
    };
  }

  // Create new hidden income
  async createHiddenIncome(data: CreateHiddenIncomeData): Promise<HiddenIncome> {
    const response = await api.post('/hidden-income', data, {
      headers: this.getHiddenModeHeaders()
    });
    return response.data.data;
  }

  // Update hidden income
  async updateHiddenIncome(id: string, data: Partial<CreateHiddenIncomeData>): Promise<HiddenIncome> {
    const response = await api.put(`/hidden-income/${id}`, data, {
      headers: this.getHiddenModeHeaders()
    });
    return response.data.data;
  }

  // Delete hidden income
  async deleteHiddenIncome(id: string): Promise<void> {
    await api.delete(`/hidden-income/${id}`, {
      headers: this.getHiddenModeHeaders()
    });
  }

  // Approve hidden income
  async approveHiddenIncome(id: string): Promise<HiddenIncome> {
    const response = await api.patch(`/hidden-income/${id}/approve`, {}, {
      headers: this.getHiddenModeHeaders()
    });
    return response.data.data;
  }

  // Get complete transaction history for a booking
  async getBookingTransactionHistory(bookingId: number): Promise<any> {
    const response = await api.get(`/hidden-income/bookings/${bookingId}/transactions`, {
      headers: this.getHiddenModeHeaders()
    });
    return response.data.data;
  }

  // On-demand decrypt for a single hidden income record
  async decryptAmounts(id: string): Promise<{ id: string; amount: string | null; collectedAmount: string | null; pendingAmount: string | null; incomeDate: string | null; }> {
    const response = await api.get(`/hidden-income/${id}/decrypt`, {
      headers: this.getHiddenModeHeaders()
    });
    return response.data.data;
  }

  // On-demand decrypt for multiple records
  async decryptAmountsBulk(ids: string[]): Promise<Array<{ id: string; amount: string | null; collectedAmount: string | null; pendingAmount: string | null; incomeDate: string | null; }>> {
    const response = await api.post(`/hidden-income/decrypt`, { ids }, {
      headers: this.getHiddenModeHeaders()
    });
    return response.data.data || [];
  }
}

export const hiddenIncomeService = new HiddenIncomeService();
export default hiddenIncomeService;
